// Shared Chat Service - Single source of truth for chat messages across all tabs
import { createCommunicationManager as commManager } from '../../../../../shared/lib/comm/gateway_ws/outbound';
import { ConversationalMessage, Attachment, MessageBlock } from './shared/attachements/conversationalTypes';
import chatApi from '../api/chatApi';
import { TokenManager } from '../../../../../shared/lib/apiClient';

export type ChatPage = 'requirements' | 'architecture' | 'code' | 'documents' | 'preview' | 'project_chat';

/** Normalized discovery index row (from message.value: Phase/Slots or phase/slots). */
export interface DiscoveryIndexPhaseRow {
    phase: string;
    slots: string[];
}

/** Extra fields for discovery channel message kinds (index, doc_summary, text). */
export interface DiscoveryMessageMeta {
    msg_type?: string;
    index_value?: DiscoveryIndexPhaseRow[];
    phase?: string;
    slot?: string;
}

/** Matches build terminal AI rows (BuildProgressTab) for chat streaming UX. */
export type ChatStreamActivityCategory = 'ai_thinking' | 'ai_tool_usage' | 'ai_text';

/** Backend `message.actions[]` → one assistant row with clickable choices (discovery / chat). */
export interface ChatPayloadMessageAction {
    id: string;
    label: string;
    style?: string;
}

export const PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE = 'payload_message_actions';

export interface ChatMessage {
    id: string;
    type: 'user' | 'assistant' | 'status';
    content: string;
    timestamp: Date;
    project_id?: string;
    session_id?: string;
    msgType?: string;
    /** Discovery channel: structured payload (mirrored under contract_data.discovery for persistence). */
    discovery?: DiscoveryMessageMeta;
    page?: ChatPage; // Page/tab identifier for filtering
    channel?: string; // Channel identifier for filtering (e.g., 'chat', 'build_feature')
    isContract?: boolean;
    contractData?: any;
    agent?: string;
    llm?: string;
    progress?: number | null;
    /** WebSocket streaming: thinking / tool_usage lines (snake_case mirrors API contract_data). */
    stream_activity_category?: ChatStreamActivityCategory;
    tool_usage_kind?: string;
    backend_payload_id?: string;
    backend_ts?: string;
    // Conversational message format (for non-requirements tabs)
    conversational?: ConversationalMessage;
    attachments?: Attachment[];
    blocks?: MessageBlock[]; // Use the flexible MessageBlock type
    /** Inbound payload `message.actions` (persisted under contract_data). */
    message_actions?: ChatPayloadMessageAction[];
    /** After user clicks a choice; buttons hide (persisted under contract_data). */
    actions_used?: boolean;
    /** Session owner from API (`user_id`); used to scope action rows to the open user. */
    backend_user_id?: string;
}

/**
 * Effective tab for filtering. Discovery channel messages always live on `project_chat`, but
 * persisted/API rows sometimes omit `page`, which would otherwise drop them from the UI.
 */
export function effectiveChatPage(msg: ChatMessage): ChatPage | undefined {
    if (msg.page != null && String(msg.page).trim() !== '') {
        return msg.page;
    }
    if (msg.channel === 'discovery') {
        return 'project_chat';
    }
    return undefined;
}

function messageMatchesChannelAndPage(
    msg: ChatMessage,
    page?: ChatPage,
    channel?: string
): boolean {
    if (channel && msg.channel !== channel) {
        return false;
    }
    if (!page) {
        return true;
    }
    return effectiveChatPage(msg) === page;
}

const CHAT_CACHE_KEY = 'vishmaker_chat_history';

/** Max thinking/tool_usage rows kept in the session (oldest dropped). Matches chat UI "Earlier" cap. */
export const MAX_STORED_STREAM_ACTIVITY_LOGS = 20;

export function isStoredStreamActivityRow(m: ChatMessage): boolean {
    return (
        m.stream_activity_category === 'ai_thinking' ||
        m.stream_activity_category === 'ai_tool_usage'
    );
}

/** Thinking / tool_usage rows are kept in memory + localStorage only, not written to DynamoDB. */
function shouldPersistMessageToDynamo(m: ChatMessage): boolean {
    return !isStoredStreamActivityRow(m);
}

function normalizeAssistantMsgType(message: ChatMessage): ChatMessage {
    if (
        message.type === 'assistant' &&
        !isStoredStreamActivityRow(message) &&
        message.message_actions &&
        message.message_actions.length > 0 &&
        (!message.msgType || String(message.msgType).trim() === '')
    ) {
        return { ...message, msgType: PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE };
    }
    // Treat normal assistant replies as text unless explicitly typed otherwise.
    if (
        message.type === 'assistant' &&
        !isStoredStreamActivityRow(message) &&
        (!message.msgType || String(message.msgType).trim() === '')
    ) {
        return { ...message, msgType: 'text' };
    }
    return message;
}

function sanitizeAttachmentsForPersistence(attachments?: Attachment[]): Array<Record<string, unknown>> | undefined {
    if (!attachments || attachments.length === 0) {
        return undefined;
    }

    const isPersistableImageUrl = (url?: string): boolean => {
        if (!url) return false;
        return url.startsWith('https://') || url.startsWith('http://') || url.startsWith('s3://');
    };

    const sanitized = attachments
        .map((attachment) => {
            if (attachment.kind === 'image') {
                const path = typeof attachment.path === 'string' ? attachment.path.trim() : '';
                const url = isPersistableImageUrl(attachment.url) ? attachment.url : undefined;

                // Avoid persisting local-only data/blob images to DynamoDB/API payloads.
                if (!path && !url) {
                    return null;
                }

                return {
                    kind: 'image',
                    mime: attachment.mime,
                    name: attachment.name,
                    ...(url ? { url } : {}),
                    ...(path ? { path } : {}),
                    ...(attachment.meta?.selection
                        ? { meta: { selection: attachment.meta.selection } }
                        : {}),
                };
            }

            if (attachment.kind === 'code') {
                return {
                    kind: 'code',
                    lang: attachment.lang,
                    text: attachment.text,
                    name: attachment.name,
                };
            }

            return attachment as unknown as Record<string, unknown>;
        })
        .filter((item): item is Record<string, unknown> => item !== null);

    return sanitized.length > 0 ? sanitized : undefined;
}

function isUploadedImageAttachment(attachment: Attachment): boolean {
    if (attachment.kind !== 'image') return false;
    const path = typeof attachment.path === 'string' ? attachment.path.trim() : '';
    const url = typeof attachment.url === 'string' ? attachment.url.trim() : '';
    const hasRemoteUrl =
        url.startsWith('https://') || url.startsWith('http://') || url.startsWith('s3://');
    return Boolean(path) || hasRemoteUrl;
}

function sanitizeAttachmentsForCache(attachments?: Attachment[]): Attachment[] | undefined {
    if (!attachments || attachments.length === 0) {
        return undefined;
    }

    return attachments.map((attachment) => {
        if (attachment.kind !== 'image') {
            return attachment;
        }

        const compactPreview = attachment.meta?.thumbnailDataUrl;
        return {
            ...attachment,
            previewUrl: compactPreview || undefined,
            meta: attachment.meta
                ? {
                      ...attachment.meta,
                      ...(compactPreview ? { thumbnailDataUrl: compactPreview } : {}),
                  }
                : compactPreview
                ? { thumbnailDataUrl: compactPreview }
                : undefined,
        };
    });
}

/** How many recent assistant "text" replies we compare for duplicate content. */
const DEDUPE_ASSISTANT_TEXT_WINDOW = 5;

/** Assistant messages from backend `type: "text"` (final reply), used for scoped duplicate detection. */
function isAssistantTextMessage(m: ChatMessage): boolean {
    return (
        m.type === 'assistant' &&
        !m.isContract &&
        !isStoredStreamActivityRow(m) &&
        m.msgType === 'text'
    );
}

/**
 * Duplicate if same id as any existing row, or (for assistant text only) same content as one of the
 * last {@link DEDUPE_ASSISTANT_TEXT_WINDOW} assistant text messages.
 */
function isDuplicateIncomingMessage(existing: ChatMessage[], incoming: ChatMessage): boolean {
    if (existing.some((x) => x.id === incoming.id)) return true;

    if (!isAssistantTextMessage(incoming)) return false;

    const peers: ChatMessage[] = [];
    for (let i = existing.length - 1; i >= 0 && peers.length < DEDUPE_ASSISTANT_TEXT_WINDOW; i--) {
        if (isAssistantTextMessage(existing[i])) peers.push(existing[i]);
    }
    return peers.some((m) => m.content === incoming.content);
}

/** Drops oldest stream-activity rows when there are more than {@link MAX_STORED_STREAM_ACTIVITY_LOGS}. */
export function trimStoredStreamActivityLogs(messages: ChatMessage[]): ChatMessage[] {
    const streamIndices: number[] = [];
    messages.forEach((m, i) => {
        if (isStoredStreamActivityRow(m)) streamIndices.push(i);
    });
    if (streamIndices.length <= MAX_STORED_STREAM_ACTIVITY_LOGS) return messages;
    const excess = streamIndices.length - MAX_STORED_STREAM_ACTIVITY_LOGS;
    const drop = new Set(streamIndices.slice(0, excess));
    return messages.filter((_, i) => !drop.has(i));
}

const STREAMING_BACKEND_TYPES = new Set(['thinking', 'tool_usage', 'text']);

/** Same heuristic as build_feature ai-activity-utils (avoid importing feature code into chat service). */
function inferToolUsageKindFromChatContent(content: string): string {
    const head = content.slice(0, 512);
    const m = head.match(/\[(?:internal|external)\]\s+(\w+)/);
    if (m?.[1]) return m[1];
    return 'Other';
}

const CATEGORY_MAP: Record<string, ChatStreamActivityCategory> = {
    thinking: 'ai_thinking',
    tool_usage: 'ai_tool_usage',
    text: 'ai_text'
};

export interface BuildStreamingChatMessagesContext {
    backend_payload_id: string;
    backend_ts?: string;
    page: ChatPage;
    channel: string;
}

/**
 * Maps VishMaker `body.kind === "messages"` items (`thinking` | `tool_usage` | `text`) to chat rows.
 * Final `text` rows are normal assistant messages; thinking/tool_usage get stream_activity_category for compact UI.
 */
export function buildStreamingActivityChatMessages(
    messages: unknown[] | undefined,
    ctx: BuildStreamingChatMessagesContext
): ChatMessage[] {
    if (!messages?.length) return [];
    const ts = ctx.backend_ts ? parseTimestamp(ctx.backend_ts) : new Date();
    const out: ChatMessage[] = [];

    messages.forEach((raw, idx) => {
        if (!raw || typeof raw !== 'object') return;
        const msg = raw as Record<string, unknown>;
        const messageType = msg.type != null ? String(msg.type) : '';
        if (!STREAMING_BACKEND_TYPES.has(messageType)) return;

        const messageContent = msg.text ?? msg.message ?? msg.content;
        if (messageContent === undefined) return;
        const contentString =
            typeof messageContent === 'string' ? messageContent : JSON.stringify(messageContent);

        const id = `${ctx.backend_payload_id}-stream-${idx}-${messageType}`;
        const category = CATEGORY_MAP[messageType] ?? 'ai_text';

        if (messageType === 'text') {
            out.push({
                id,
                type: 'assistant',
                content: contentString,
                msgType: 'text',
                timestamp: ts,
                isContract: false,
                page: ctx.page,
                channel: ctx.channel,
                backend_payload_id: ctx.backend_payload_id,
                backend_ts: ctx.backend_ts
            });
            return;
        }

        out.push({
            id,
            type: 'assistant',
            content: contentString,
            timestamp: ts,
            isContract: false,
            page: ctx.page,
            channel: ctx.channel,
            stream_activity_category: category,
            tool_usage_kind:
                category === 'ai_tool_usage' ? inferToolUsageKindFromChatContent(contentString) : undefined,
            backend_payload_id: ctx.backend_payload_id,
            backend_ts: ctx.backend_ts
        });
    });

    return out;
}

/** Parse timestamp from API/cache - handles ISO strings, Unix seconds, and created_at fallback. Never returns "now" for historical messages. */
function parseTimestamp(val: unknown, fallback?: string): Date {
    const tryParse = (v: unknown): Date | null => {
        if (v instanceof Date && !isNaN(v.getTime())) return v;
        if (v == null) return null;
        const d = new Date(v as string | number);
        if (!isNaN(d.getTime())) return d;
        const num = typeof v === 'string' ? parseInt(v, 10) : (typeof v === 'number' ? v : NaN);
        if (!isNaN(num)) {
            const ms = num > 1e12 ? num : num * 1000; // seconds vs milliseconds
            const parsed = new Date(ms);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        return null;
    };
    return tryParse(val) ?? tryParse(fallback) ?? new Date(0);
}

const CURRENT_SESSION_KEY = 'vishmaker_current_chat_session';
const CHAT_CACHE_EXPIRY_DAYS = 5;

let globalListeners: Set<(messages: ChatMessage[]) => void> = new Set();
let currentSessionId: string | null = null;
let currentProjectId: string | null = null;
let sessionListeners: Set<(sessionId: string | null) => void> = new Set();

/** Avoid duplicate GET /sessions + GET /messages when multiple hooks mount or Strict Mode re-runs effects. */
const sessionResolveInflight = new Map<string, Promise<string>>();
const initProjectInflight = new Map<string, Promise<ChatMessage[]>>();
let bareInitInflight: Promise<ChatMessage[]> | null = null;
/** Last project for which we hydrated local chat cache from the backend (session + messages). */
let chatHistoryLoadedForProjectId: string | null = null;

function parseIsoDateMs(value: unknown): number {
    if (typeof value !== 'string' || value.trim() === '') return 0;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : 0;
}

function pickBestSessionForProject(
    sessions: Array<{ id: string; updated_at?: string; created_at?: string }>,
    projectIdStr: string
): string | null {
    if (!sessions.length) return null;

    // Keep continuity after reloads when we already know the project+session pair.
    try {
        const cached = localStorage.getItem(CURRENT_SESSION_KEY);
        if (cached) {
            const parsed = JSON.parse(cached) as { sessionId?: string; projectId?: string };
            if (parsed.projectId === projectIdStr && parsed.sessionId) {
                const hasCached = sessions.some((s) => s.id === parsed.sessionId);
                if (hasCached) return parsed.sessionId;
            }
        }
    } catch {
        // Ignore malformed cache and fall back to deterministic session selection.
    }

    const sorted = [...sessions].sort((a, b) => {
        const aMs = parseIsoDateMs(a.updated_at) || parseIsoDateMs(a.created_at);
        const bMs = parseIsoDateMs(b.updated_at) || parseIsoDateMs(b.created_at);
        return bMs - aMs;
    });
    return sorted[0]?.id ?? sessions[0].id;
}

function getCachedSessionIdForProject(projectIdStr: string): string | null {
    try {
        const cached = localStorage.getItem(CURRENT_SESSION_KEY);
        if (!cached) return null;
        const parsed = JSON.parse(cached) as { sessionId?: string; projectId?: string };
        if (parsed.projectId === projectIdStr && parsed.sessionId) {
            return parsed.sessionId;
        }
    } catch {
        // Ignore malformed cache.
    }
    return null;
}

/**
 * When switching projects, drop prior project's discovery rows so they are not merged into the new session.
 */
function pruneDiscoveryProjectChatForProjectSwitch(nextProjectIdStr: string): void {
    if (
        chatHistoryLoadedForProjectId == null ||
        chatHistoryLoadedForProjectId === nextProjectIdStr
    ) {
        return;
    }
    const currentMessages = loadChatHistoryFromLocalStorage();
    const nextMessages = currentMessages.filter(
        (m) =>
            !(m.channel === 'discovery' && effectiveChatPage(m) === 'project_chat')
    );
    saveChatHistoryToLocalStorage(nextMessages);
}

/**
 * Dynamo hydrate can complete after WS already appended discovery rows (index/TOC, doc_summary, stream).
 * Union server rows with those in-memory rows not yet in Dynamo (by id).
 */
function mergeDiscoveryInFlightWithLoaded(
    loadedMessages: ChatMessage[],
    projectIdStr: string,
    sessionId: string
): ChatMessage[] {
    const loadedIds = new Set(loadedMessages.map((m) => m.id));
    const currentMessages = loadChatHistoryFromLocalStorage();
    const extras = currentMessages.filter((m) => {
        if (loadedIds.has(m.id)) return false;
        if (m.channel !== 'discovery' || effectiveChatPage(m) !== 'project_chat') return false;
        if (String(m.project_id ?? '') !== projectIdStr) return false;
        return String(m.session_id ?? '') === sessionId;
    });
    const byId = new Map<string, ChatMessage>();
    for (const m of loadedMessages) {
        byId.set(m.id, m);
    }
    for (const m of extras) {
        if (!byId.has(m.id)) {
            byId.set(m.id, m);
        }
    }
    return Array.from(byId.values()).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

// Load chat history from localStorage (cache)
const loadChatHistoryFromLocalStorage = (): ChatMessage[] => {
    try {
        const cached = localStorage.getItem(CHAT_CACHE_KEY);
        if (cached) {
            const { messages, timestamp } = JSON.parse(cached);
            const now = new Date().getTime();
            const expiryTime = timestamp + (CHAT_CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
            
            if (now < expiryTime) {
                const mapped = messages.map((msg: any) => {
                    const base = { ...msg, timestamp: parseTimestamp(msg.timestamp) };
                    if (base.page == null && base.channel === 'discovery') {
                        return { ...base, page: 'project_chat' as ChatPage };
                    }
                    return base;
                });
                return trimStoredStreamActivityLogs(mapped);
            } else {
                localStorage.removeItem(CHAT_CACHE_KEY);
            }
        }
    } catch (error) {
        console.error('Failed to load chat history from localStorage:', error);
        localStorage.removeItem(CHAT_CACHE_KEY);
    }
    return [];
};

/** Stream-activity rows from the chat cache (for merging after Dynamo load; same TTL as full cache). */
function readEphemeralStreamRowsFromLocalStorage(sessionId?: string, projectIdStr?: string): ChatMessage[] {
    try {
        const cached = localStorage.getItem(CHAT_CACHE_KEY);
        if (!cached) return [];
        const { messages, timestamp } = JSON.parse(cached);
        const now = new Date().getTime();
        const expiryTime = timestamp + CHAT_CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        if (now >= expiryTime) return [];

        return (messages as any[])
            .map((msg) => ({
                ...msg,
                timestamp: parseTimestamp(msg.timestamp),
            }))
            .filter((m) => {
                if (sessionId && String((m as ChatMessage).session_id ?? '') !== sessionId) return false;
                if (projectIdStr && String((m as ChatMessage).project_id ?? '') !== projectIdStr) return false;
                return true;
            })
            .filter((m) => isStoredStreamActivityRow(m as ChatMessage)) as ChatMessage[];
    } catch {
        return [];
    }
}

// Load chat history from DynamoDB session
export const loadChatHistory = async (sessionId?: string): Promise<ChatMessage[]> => {
    // If no session ID, fall back to localStorage
    if (!sessionId) {
        return loadChatHistoryFromLocalStorage();
    }

    try {
        const token = TokenManager.getToken();
        if (token) {
            // Load from DynamoDB (apiClient refreshes expired access tokens before the request)
            const messages = await chatApi.getSessionMessages(sessionId);
            
            // Convert API response to ChatMessage format and sort by timestamp to maintain order
            const chatMessages: ChatMessage[] = messages
                .map((msg: any) => {
                    const fromContractType =
                        (msg.contract_data?.vish_msg_type as string | undefined) ??
                        (msg.contract_data?.discovery as DiscoveryMessageMeta | undefined)?.msg_type;
                    const message_actions = msg.contract_data?.message_actions as
                        | ChatPayloadMessageAction[]
                        | undefined;
                    let msgType = fromContractType;
                    if (
                        !msgType &&
                        Array.isArray(message_actions) &&
                        message_actions.length > 0
                    ) {
                        msgType = PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE;
                    }
                    const mapped = {
                    id: msg.id,
                    type: msg.type as 'user' | 'assistant' | 'status',
                    content: msg.content,
                    timestamp: parseTimestamp(msg.timestamp, msg.created_at),
                    project_id: msg.project_id,
                    session_id: msg.session_id,
                    page:
                        (msg.page as ChatPage | undefined) ??
                        (msg.channel === 'discovery' ? ('project_chat' as ChatPage) : undefined),
                    channel: msg.channel,
                    isContract: msg.is_contract,
                    contractData: msg.contract_data,
                    discovery: msg.contract_data?.discovery as DiscoveryMessageMeta | undefined,
                    agent: msg.agent,
                    llm: msg.llm,
                    progress: msg.progress,
                    stream_activity_category: msg.contract_data?.stream_activity_category as
                        | ChatStreamActivityCategory
                        | undefined,
                    tool_usage_kind: msg.contract_data?.tool_usage_kind as string | undefined,
                    backend_payload_id: msg.contract_data?.backend_payload_id as string | undefined,
                    backend_ts: msg.contract_data?.backend_ts as string | undefined,
                    conversational: msg.conversational,
                    attachments: msg.attachments,
                    blocks: msg.blocks,
                    message_actions,
                    actions_used: Boolean(msg.contract_data?.actions_used),
                    backend_user_id:
                        typeof msg.user_id === 'string' && msg.user_id.trim() !== ''
                            ? msg.user_id
                            : undefined,
                    msgType,
                    };
                    return normalizeAssistantMsgType(mapped);
                })
                .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()); // Maintain sequence order

            const projectIdStr =
                chatMessages.find((m) => m.project_id != null)?.project_id != null
                    ? String(chatMessages.find((m) => m.project_id != null)!.project_id)
                    : undefined;
            const ephemeral = readEphemeralStreamRowsFromLocalStorage(sessionId, projectIdStr);
            const apiIds = new Set(chatMessages.map((m) => m.id));
            const extraStream = ephemeral.filter((e) => !apiIds.has(e.id));
            const merged = [...chatMessages, ...extraStream].sort(
                (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
            );
            const trimmed = trimStoredStreamActivityLogs(merged);

            // Cache in localStorage (includes ephemeral stream rows not in Dynamo)
            saveChatHistoryToLocalStorage(trimmed);
            return trimmed;
        }
    } catch (error) {
        console.error('Failed to load chat history from DynamoDB, falling back to localStorage:', error);
    }
    
    // Fallback to localStorage
    return loadChatHistoryFromLocalStorage();
};

// Save chat history to localStorage (cache)
const saveChatHistoryToLocalStorage = (messages: ChatMessage[]) => {
    try {
        const cacheData = {
            messages: messages.map(msg => {
                const d = msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp as string | number);
                const iso = !isNaN(d.getTime()) ? d.toISOString() : new Date(0).toISOString();
                return {
                    ...msg,
                    timestamp: iso,
                    attachments: sanitizeAttachmentsForCache(msg.attachments),
                };
            }),
            timestamp: new Date().getTime()
        };
        localStorage.setItem(CHAT_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
        console.error('Failed to save chat history to localStorage:', error);
    }
};

// Save a single message to DynamoDB session immediately (non-blocking)
const saveMessageToSession = async (
    message: ChatMessage,
    sessionId: string,
    projectIdStr: string
) => {
    try {
        if (!shouldPersistMessageToDynamo(message)) return;

        const token = TokenManager.getToken();
        if (!token) {
            console.warn('Cannot save to DynamoDB: No access token');
            return;
        }

        const pageToPersist = effectiveChatPage(message) ?? ('requirements' as ChatPage);
        const hasImageAttachments = Boolean(
            message.attachments?.some((att) => att.kind === 'image')
        );
        const uploadedImageCount = message.attachments?.filter(isUploadedImageAttachment).length ?? 0;
        const totalImageCount = message.attachments?.filter((att) => att.kind === 'image').length ?? 0;

        if (hasImageAttachments) {
            console.log('🧾 [Chat] Pre-persist attachment upload validation:', {
                message_id: message.id,
                session_id: sessionId,
                project_id: projectIdStr,
                total_image_attachments: totalImageCount,
                uploaded_image_attachments: uploadedImageCount
            });
            if (uploadedImageCount < totalImageCount) {
                console.warn('⚠️ [Chat] Some image attachments are not uploaded (no S3 path/remote URL). They will be excluded from DynamoDB payload.', {
                    message_id: message.id
                });
            }
        }

        // Convert to API format (discovery meta stored under contract_data.discovery)
        const mergedContract = {
            ...(message.contractData || {}),
            ...(message.discovery ? { discovery: message.discovery } : {}),
            ...(message.msgType ? { vish_msg_type: message.msgType } : {}),
            ...(message.stream_activity_category
                ? { stream_activity_category: message.stream_activity_category }
                : {}),
            ...(message.tool_usage_kind ? { tool_usage_kind: message.tool_usage_kind } : {}),
            ...(message.backend_payload_id ? { backend_payload_id: message.backend_payload_id } : {}),
            ...(message.backend_ts ? { backend_ts: message.backend_ts } : {}),
            ...(message.message_actions && message.message_actions.length > 0
                ? { message_actions: message.message_actions }
                : {}),
            ...(message.actions_used ? { actions_used: true } : {}),
        };
        const payload = {
            session_id: sessionId,
            project_id: projectIdStr,
            content: message.content,
            type: message.type,
            page: pageToPersist as any,
            channel: message.channel,
            is_contract: message.isContract,
            contract_data: Object.keys(mergedContract).length > 0 ? mergedContract : undefined,
            agent: message.agent,
            llm: message.llm,
            progress: message.progress,
            conversational: message.conversational,
            attachments: sanitizeAttachmentsForPersistence(message.attachments),
            blocks: message.blocks,
        };

        if (hasImageAttachments) {
            const persistedCount = payload.attachments?.filter((att: any) => att?.kind === 'image').length ?? 0;
            console.log('📦 [Chat] Final DynamoDB message payload attachment summary:', {
                message_id: message.id,
                persisted_image_attachments: persistedCount
            });
        }

        await chatApi.createMessage(payload);
    } catch (error) {
        console.error('Failed to save message to DynamoDB session:', error);
        // Don't throw - we still want to save to localStorage
    }
};

/** Resolve session id for a project (list sessions, then create if none). Single-flight per projectId. */
async function resolveSessionForProject(projectIdStr: string): Promise<string> {
    const token = TokenManager.getToken();
    if (!token) {
        console.warn('Cannot create session: No access token');
        return '';
    }

    // Primary source requested by product flow: use cached project-session pair directly.
    const cachedSessionId = getCachedSessionIdForProject(projectIdStr);
    if (cachedSessionId) {
        currentSessionId = cachedSessionId;
        currentProjectId = projectIdStr;
        sessionListeners.forEach((listener) => listener(cachedSessionId));
        return cachedSessionId;
    }

    const sessions = await chatApi.getSessions(projectIdStr);

    let sessionId: string;
    const preferredSessionId = pickBestSessionForProject(sessions, projectIdStr);
    if (preferredSessionId) {
        sessionId = preferredSessionId;
        console.log(`Using preferred session: ${sessionId} for project ${projectIdStr}`);
    } else {
        const session = await chatApi.createSession({ project_id: projectIdStr });
        sessionId = session.id;
        console.log(`Created new session: ${sessionId} for project ${projectIdStr}`);
    }

    currentSessionId = sessionId;
    currentProjectId = projectIdStr;
    localStorage.setItem(CURRENT_SESSION_KEY, JSON.stringify({ sessionId, projectId: projectIdStr }));
    sessionListeners.forEach((listener) => listener(sessionId));
    return sessionId;
}

// Create or get session for a project
export const createOrGetSession = async (projectId: string | number): Promise<string> => {
    const projectIdStr = String(projectId);

    if (currentSessionId && currentProjectId === projectIdStr) {
        return currentSessionId;
    }

    let inflight = sessionResolveInflight.get(projectIdStr);
    if (!inflight) {
        inflight = resolveSessionForProject(projectIdStr)
            .catch((error) => {
                console.error('Failed to create or get session:', error);
                return '';
            })
            .finally(() => {
                sessionResolveInflight.delete(projectIdStr);
            });
        sessionResolveInflight.set(projectIdStr, inflight);
    }
    return inflight;
};

async function initializeChatHistoryForProject(projectIdStr: string): Promise<ChatMessage[]> {
    pruneDiscoveryProjectChatForProjectSwitch(projectIdStr);
    const sessionId = await createOrGetSession(projectIdStr);
    let resolvedMessages: ChatMessage[];
    if (sessionId) {
        const loadedMessages = await loadChatHistory(sessionId);
        resolvedMessages = trimStoredStreamActivityLogs(
            mergeDiscoveryInFlightWithLoaded(loadedMessages, projectIdStr, sessionId)
        );
    } else {
        resolvedMessages = loadChatHistoryFromLocalStorage();
    }
    saveChatHistoryToLocalStorage(resolvedMessages);
    chatHistoryLoadedForProjectId = projectIdStr;
    console.log(`SharedChatService: Initialized with ${resolvedMessages.length} messages`);
    globalListeners.forEach((listener) => listener([...resolvedMessages]));
    return resolvedMessages;
}

/**
 * Re-fetch session messages from the server and merge with any in-flight discovery rows.
 * Call after index/TOC (or other WS-only rows) so Dynamo has caught up and the live document stays in sync.
 */
export async function pullLatestSessionMessagesForProject(
    projectId: string | number
): Promise<ChatMessage[]> {
    const projectIdStr = String(projectId);
    pruneDiscoveryProjectChatForProjectSwitch(projectIdStr);
    const sessionId = await createOrGetSession(projectIdStr);
    if (!sessionId) {
        const cached = loadChatHistoryFromLocalStorage();
        globalListeners.forEach((listener) => listener([...cached]));
        return cached;
    }
    const loadedMessages = await loadChatHistory(sessionId);
    const merged = trimStoredStreamActivityLogs(
        mergeDiscoveryInFlightWithLoaded(loadedMessages, projectIdStr, sessionId)
    );
    saveChatHistoryToLocalStorage(merged);
    chatHistoryLoadedForProjectId = projectIdStr;
    globalListeners.forEach((listener) => listener([...merged]));
    return merged;
}

async function initializeChatHistoryBare(): Promise<ChatMessage[]> {
    const cachedMessages = loadChatHistoryFromLocalStorage();
    try {
        const cached = localStorage.getItem(CURRENT_SESSION_KEY);
        if (cached) {
            const { sessionId, projectId: cachedProjectId } = JSON.parse(cached);
            currentSessionId = sessionId;
            currentProjectId = cachedProjectId;
        }
    } catch (error) {
        console.error('Failed to restore session from cache:', error);
    }
    console.log(`SharedChatService: Initialized with ${cachedMessages.length} messages`);
    globalListeners.forEach((listener) => listener([...cachedMessages]));
    return cachedMessages;
}

// Initialize chat history with a project (creates/loads session)
export const initializeChatHistory = async (projectId?: string | number): Promise<ChatMessage[]> => {
    const hasProject =
        projectId !== undefined && projectId !== null && String(projectId).trim() !== '';

    if (hasProject) {
        const projectIdStr = String(projectId);
        if (
            chatHistoryLoadedForProjectId === projectIdStr &&
            currentProjectId === projectIdStr &&
            currentSessionId
        ) {
            return loadChatHistoryFromLocalStorage();
        }
        let inflight = initProjectInflight.get(projectIdStr);
        if (!inflight) {
            inflight = initializeChatHistoryForProject(projectIdStr).finally(() => {
                initProjectInflight.delete(projectIdStr);
            });
            initProjectInflight.set(projectIdStr, inflight);
        }
        return inflight;
    }

    let inflight = bareInitInflight;
    if (!inflight) {
        inflight = initializeChatHistoryBare().finally(() => {
            bareInitInflight = null;
        });
        bareInitInflight = inflight;
    }
    return inflight;
};

// Get current chat messages (optionally filtered by page and channel)
export const getChatMessages = (page?: ChatPage, channel?: string): ChatMessage[] => {
    const messages = [...loadChatHistoryFromLocalStorage()];
    if (!page && !channel) {
        return messages;
    }
    return messages.filter((msg) => messageMatchesChannelAndPage(msg, page, channel));
};

/**
 * Backfill currently loaded rows to a project's DynamoDB session.
 * Useful when messages were created before a project id existed (e.g. discovery bootstrap).
 */
export const persistCurrentMessagesToProject = (
    projectId: string | number,
    page?: ChatPage,
    channel?: string
): void => {
    const projectIdStr = String(projectId);
    const filtered = getChatMessages(page, channel).filter(shouldPersistMessageToDynamo);
    if (filtered.length === 0) return;
    persistMessagesForProject(filtered, projectIdStr);
};

/**
 * Persist messages to DynamoDB for a project: use session id only if it was resolved for this project;
 * otherwise list sessions for the project (or create one), then save each message to that session.
 */
function persistMessagesForProject(messages: ChatMessage[], projectIdStr: string): void {
    const sessionMatchesProject =
        Boolean(currentSessionId) && currentProjectId === projectIdStr;
    const saveAll = (sessionId: string) => {
        messages.forEach((message) => {
            if (!shouldPersistMessageToDynamo(message)) return;
            saveMessageToSession(message, sessionId, projectIdStr).catch((err) => {
                console.error('Failed to save message to session:', err);
            });
        });
    };
    if (sessionMatchesProject) {
        saveAll(currentSessionId!);
        return;
    }
    createOrGetSession(projectIdStr).then((sessionId) => {
        if (sessionId) saveAll(sessionId);
    });
}

// Add a message to chat history - saves immediately to session if available
export const addChatMessage = (message: ChatMessage, projectId?: string | number) => {
    const currentMessages = loadChatHistoryFromLocalStorage();
    const projectIdToUse = projectId ? String(projectId) : currentProjectId;
    const normalizedMessage: ChatMessage = normalizeAssistantMsgType({
        ...message,
        project_id: message.project_id ?? projectIdToUse ?? undefined,
        page: effectiveChatPage(message) ?? ('requirements' as ChatPage),
    });
    if (isDuplicateIncomingMessage(currentMessages, normalizedMessage)) {
        return currentMessages;
    }

    const nextMessages = trimStoredStreamActivityLogs([...currentMessages, normalizedMessage]);

    // Always save to localStorage cache
    saveChatHistoryToLocalStorage(nextMessages);

    if (projectIdToUse && shouldPersistMessageToDynamo(message)) {
        persistMessagesForProject([normalizedMessage], projectIdToUse);
    }
    
    // Notify all listeners
    globalListeners.forEach(listener => listener([...nextMessages]));
    return nextMessages;
};

// Add multiple messages - saves each to session if available
export const addChatMessages = (messages: ChatMessage[], projectId?: string | number) => {
    const currentMessages = loadChatHistoryFromLocalStorage();
    const base = [...currentMessages];
    const accepted: ChatMessage[] = [];
    const projectIdToUse = projectId ? String(projectId) : currentProjectId;
    for (const m of messages) {
        const normalizedMessage: ChatMessage = normalizeAssistantMsgType({
            ...m,
            project_id: m.project_id ?? projectIdToUse ?? undefined,
            page: effectiveChatPage(m) ?? ('requirements' as ChatPage),
        });
        const combined = [...base, ...accepted];
        if (!isDuplicateIncomingMessage(combined, normalizedMessage)) {
            accepted.push(normalizedMessage);
        }
    }
    if (accepted.length === 0) {
        return currentMessages;
    }

    const nextMessages = trimStoredStreamActivityLogs([...currentMessages, ...accepted]);

    // Always save to localStorage cache
    saveChatHistoryToLocalStorage(nextMessages);
    
    const toPersist = accepted.filter(shouldPersistMessageToDynamo);
    if (projectIdToUse && toPersist.length > 0) {
        persistMessagesForProject(toPersist, projectIdToUse);
    }
    
    // Notify all listeners
    globalListeners.forEach(listener => listener([...nextMessages]));
    return nextMessages;
};

// Set chat messages (replace all)
export const setChatMessages = (messages: ChatMessage[]) => {
    chatHistoryLoadedForProjectId = null;
    const nextMessages = trimStoredStreamActivityLogs([...messages]);
    saveChatHistoryToLocalStorage(nextMessages);
    // Notify all listeners
    globalListeners.forEach(listener => listener([...nextMessages]));
    return nextMessages;
};

/** Remove a single message by id (e.g. client-only UI rows). Notifies listeners and updates cache. */
export const removeChatMessageById = (messageId: string): ChatMessage[] => {
    const nextMessages = loadChatHistoryFromLocalStorage().filter((msg) => msg.id !== messageId);
    saveChatHistoryToLocalStorage(nextMessages);
    globalListeners.forEach((listener) => listener([...nextMessages]));
    return nextMessages;
};

/** Marks a payload-actions assistant row as consumed (hides buttons). Updates cache + subscribers. */
export function markChatMessageActionsUsed(messageId: string): void {
    const currentMessages = loadChatHistoryFromLocalStorage();
    let changed = false;
    const nextMessages = currentMessages.map((m) => {
        if (m.id !== messageId) return m;
        if (m.actions_used) return m;
        changed = true;
        return { ...m, actions_used: true };
    });
    if (!changed) return;
    saveChatHistoryToLocalStorage(nextMessages);
    globalListeners.forEach((listener) => listener([...nextMessages]));
}

// Clear chat history (optionally filtered by page)
export const clearChatHistory = (page?: ChatPage) => {
    if (page) {
        // Only clear messages for the specified page
        const currentMessages = loadChatHistoryFromLocalStorage();
        const nextMessages = trimStoredStreamActivityLogs(
            currentMessages.filter(msg => msg.page !== page)
        );
        // Discovery create-flow clears only project_chat rows; reset hydrate marker so
        // first project switch does not prune freshly bootstrapped discovery messages.
        if (page === 'project_chat') {
            chatHistoryLoadedForProjectId = null;
        }
        saveChatHistoryToLocalStorage(nextMessages);
        // Notify all listeners with updated messages
        globalListeners.forEach(listener => listener([...nextMessages]));
    } else {
        // Clear all messages if no page specified
        localStorage.removeItem(CHAT_CACHE_KEY);
        chatHistoryLoadedForProjectId = null;
        // Notify all listeners
        globalListeners.forEach(listener => listener([]));
    }
};

// Get all sessions for a project
export const getChatSessions = async (projectId?: string | number): Promise<any[]> => {
    try {
        const token = TokenManager.getToken();
        if (!token) {
            return [];
        }

        const projectIdStr = projectId ? String(projectId) : undefined;
        return await chatApi.getSessions(projectIdStr);
    } catch (error) {
        console.error('Failed to get chat sessions:', error);
        return [];
    }
};

// Load a specific session
export const loadSession = async (sessionId: string): Promise<ChatMessage[]> => {
    try {
        const messages = await loadChatHistory(sessionId);
        currentSessionId = sessionId;
        
        // Get session details to set projectId
        const session = await chatApi.getSession(sessionId);
        currentProjectId = session.project_id;
        chatHistoryLoadedForProjectId = session.project_id;
        localStorage.setItem(CURRENT_SESSION_KEY, JSON.stringify({ sessionId, projectId: session.project_id }));
        
        saveChatHistoryToLocalStorage(messages);
        
        // Notify all listeners
        globalListeners.forEach(listener => listener([...messages]));
        sessionListeners.forEach(listener => listener(sessionId));
        
        return messages;
    } catch (error) {
        console.error('Failed to load session:', error);
        return [];
    }
};

// Get current session ID
export const getCurrentSessionId = (): string | null => {
    return currentSessionId;
};

// Get current project ID
export const getCurrentProjectId = (): string | null => {
    return currentProjectId;
};

// Subscribe to session changes
export const subscribeToSession = (listener: (sessionId: string | null) => void): (() => void) => {
    sessionListeners.add(listener);
    // Immediately call with current session
    listener(currentSessionId);
    // Return unsubscribe function
    return () => {
        sessionListeners.delete(listener);
    };
};

// Subscribe to chat message changes (optionally filtered by page and channel)
export const subscribeToChatMessages = (
    listener: (messages: ChatMessage[]) => void,
    page?: ChatPage,
    channel?: string
): (() => void) => {
    const filteredListener = (messages: ChatMessage[]) => {
        const filtered =
            page || channel
                ? messages.filter((msg) => messageMatchesChannelAndPage(msg, page, channel))
                : [...messages];

        listener(filtered);
    };
    globalListeners.add(filteredListener);
    // Immediately call with current messages (filtered if page/channel specified)
    filteredListener(loadChatHistoryFromLocalStorage());
    // Return unsubscribe function
    return () => {
        globalListeners.delete(filteredListener);
    };
};

// Get cache key (for compatibility)
export const getChatCacheKey = () => CHAT_CACHE_KEY;

